/* VASUKI — premium storefront interactions */
(() => {
  'use strict';
  const routes = window.routes || {};

  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => [...r.querySelectorAll(s)];

  function money(cents, format) {
    let formatted;
    if (window.Shopify?.formatMoney) formatted = Shopify.formatMoney(cents, format);
    else {
      const amount = (Number(cents || 0) / 100).toFixed(2);
      formatted = amount.endsWith('.00') ? `₹${amount.slice(0, -3)}` : `₹${amount}`;
    }
    if (window.currencyCodeEnabled && window.shopCurrency) {
      formatted = `${formatted} ${window.shopCurrency}`;
    }
    return formatted;
  }

  function updateCartCount(cart) {
    qsa('[data-cart-count]').forEach(el => {
      const count = Number(cart?.item_count || 0);
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  async function getCart() {
    const res = await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Cart unavailable');
    return res.json();
  }

  function openDrawer() {
    qs('[data-cart-drawer]')?.classList.add('open');
    qs('.cart-drawer__overlay')?.classList.add('open');
    document.body.classList.add('drawer-open');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    qs('[data-cart-drawer]')?.classList.remove('open');
    qs('.cart-drawer__overlay')?.classList.remove('open');
    document.body.classList.remove('drawer-open');
    document.body.style.overflow = '';
  }

  function bindCartTriggers() {
    qsa('[data-cart-open]').forEach(btn => {
      if (btn.dataset.cartBound) return;
      btn.dataset.cartBound = 'true';
      btn.addEventListener('click', e => { e.preventDefault(); openDrawer(); });
    });
    qsa('[data-cart-close]').forEach(btn => {
      if (btn.dataset.cartBound) return;
      btn.dataset.cartBound = 'true';
      btn.addEventListener('click', e => { e.preventDefault(); closeDrawer(); });
    });
  }

  async function refreshCartDrawer(openAfter = false) {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    try {
      const res = await fetch(`${window.location.pathname}?section_id=cart-drawer`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (res.ok) {
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshDrawer = doc.querySelector('[data-cart-drawer]');
        const freshOverlay = doc.querySelector('.cart-drawer__overlay');
        if (freshDrawer) drawer.replaceWith(freshDrawer);
        if (freshOverlay) qs('.cart-drawer__overlay')?.replaceWith(freshOverlay);
      }
      updateCartCount(await getCart());
      bindCartTriggers();
      bindCartQuantity();
      bindCartNote();
      if (openAfter) openDrawer();
    } catch (err) { console.warn('Cart refresh failed', err); }
  }

  async function addToCart(form) {
    const button = qs('[type="submit"]', form);
    const original = button?.innerHTML;
    button && (button.disabled = true, button.innerHTML = 'Adding…');
    try {
      const res = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || 'Could not add this item to your bag.');
      await refreshCartDrawer(true);
    } catch (err) { alert(err.message || 'Could not add to cart. Please try again.'); }
    finally { if (button) { button.disabled = false; button.innerHTML = original || 'Add to Cart'; } }
  }

  async function changeCartLine(line, quantity) {
    try {
      const res = await fetch(`${routes.cart_change_url || '/cart/change'}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!res.ok) throw new Error('Cart update failed');
      await refreshCartDrawer(false);
    } catch (err) { console.warn(err); }
  }

  function bindCartQuantity() {
    qsa('[data-cart-drawer] [data-quantity-plus], [data-cart-drawer] [data-quantity-minus]').forEach(btn => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', e => {
        e.preventDefault();
        const wrap = btn.closest('[data-cart-line]');
        const input = qs('input[type="number"]', wrap);
        if (!wrap || !input) return;
        const line = Number(wrap.dataset.cartLine);
        const next = btn.hasAttribute('data-quantity-plus') ? Number(input.value) + 1 : Number(input.value) - 1;
        input.value = Math.max(0, Math.min(99, next));
        changeCartLine(line, Number(input.value));
      });
    });
  }

  function bindCartNote() {
    const note = qs('#CartDrawerNote');
    if (!note || note.dataset.bound) return;
    note.dataset.bound = 'true';
    let timer;
    note.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => fetch('/cart/update.js', { method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'}, body:JSON.stringify({note:note.value}) }), 400);
    });
  }

  function bindQuantity() {
    qsa('[data-quantity-plus], [data-quantity-minus]').forEach(btn => {
      if (btn.closest('[data-cart-drawer]') || btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', () => {
        const input = btn.closest('.quantity-selector')?.querySelector('input[type="number"]');
        if (!input) return;
        const min = Number(input.min || 1), max = Number(input.max || 99), cur = Number(input.value || 1);
        input.value = btn.hasAttribute('data-quantity-plus') ? Math.min(max, cur + 1) : Math.max(min, cur - 1);
      });
    });
  }

  function initProduct(root) {
    if (!root || root.dataset.productReady) return;
    root.dataset.productReady = 'true';
    const variantsEl = qs('[data-product-variants]', root);
    const variants = variantsEl ? JSON.parse(variantsEl.textContent || '[]') : [];
    const form = qs('[data-product-form]', root);
    if (!form || !variants.length) return;
    const selects = qsa('[data-option-select]', root);
    const groups = qsa('[data-option-group]', root);
    const idInput = qs('input[name="id"]', form);
    const priceEl = qs('[id^="ProductPrice-"]', root);
    const compareEl = qs('[id^="ProductComparePrice-"]', root);
    const saleEl = qs('[id^="ProductSaleBadge-"]', root);
    const submit = qs('.product-submit', form);
    const moneyFormat = window.themeMoneyFormat || window.Shopify?.money_format;

    const selected = () => selects.map(s => s.value);
    const find = opts => variants.find(v => v.options.every((x, i) => x === opts[i]));

    function updateButtons() {
      const chosen = selected();
      groups.forEach((group, index) => {
        qsa('[data-option-value]', group).forEach(btn => {
          const value = btn.dataset.value;
          const possible = variants.some(v => v.available && v.options.every((x, i) => i === index ? x === value : x === chosen[i]));
          btn.disabled = !possible;
          btn.classList.toggle('disabled', !possible);
          btn.setAttribute('aria-disabled', String(!possible));
          btn.classList.toggle('selected', chosen[index] === value);
        });
      });
    }

    function updateVariant() {
      const variant = find(selected());
      if (!variant) {
        if (submit) { submit.disabled = true; submit.textContent = 'Unavailable'; }
        updateButtons(); return;
      }
      idInput.value = variant.id;
      if (priceEl) priceEl.innerHTML = money(variant.price, moneyFormat);
      const onSale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
      if (compareEl) { compareEl.innerHTML = onSale ? money(variant.compare_at_price, moneyFormat) : ''; compareEl.hidden = !onSale; }
      if (saleEl) saleEl.hidden = !onSale;
      if (submit) { submit.disabled = !variant.available; submit.textContent = variant.available ? 'Add to Cart' : 'Sold Out'; }
      groups.forEach((g, i) => { const label = qs('[data-selected-label]', g); if (label) label.textContent = selected()[i] || ''; });
      if (variant.featured_media?.preview_image?.src) {
        const img = qs('[id^="ProductMainImage-"]', root);
        if (img) img.src = `${variant.featured_media.preview_image.src}${variant.featured_media.preview_image.src.includes('?') ? '&' : '?'}width=1400`;
      }
      if (window.history?.replaceState) {
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); window.history.replaceState({}, '', url);
      }
      updateButtons();
    }

    qsa('[data-option-value]', root).forEach(btn => btn.addEventListener('click', () => {
      if (btn.disabled) return;
      const index = Number(btn.dataset.optionIndex), select = selects[index];
      if (!select) return;
      select.value = btn.dataset.value;
      updateVariant();
    }));
    qsa('[data-product-media]', root).forEach(btn => btn.addEventListener('click', () => {
      const img = qs('[id^="ProductMainImage-"]', root); if (img) img.src = btn.dataset.full;
      qsa('[data-product-media]', root).forEach(x => x.classList.remove('active')); btn.classList.add('active');
    }));
    updateVariant();
  }

  function initSizeCharts() {
    qsa('[data-size-chart]').forEach(chart => {
      if (chart.dataset.ready) return; chart.dataset.ready = 'true';
      const open = () => { chart.classList.add('is-open'); chart.setAttribute('aria-hidden','false'); document.body.style.overflow='hidden'; };
      const close = () => { chart.classList.remove('is-open'); chart.setAttribute('aria-hidden','true'); if (!qs('.cart-drawer.open')) document.body.style.overflow=''; };
      const trigger = qs('[data-size-chart-open]'); trigger?.addEventListener('click', open);
      qsa('[data-size-chart-close]', chart).forEach(x => x.addEventListener('click', close));
    });
  }


  function bindMobileMenu() {
    const menu = qs('[data-mobile-menu]');
    if (!menu) return;
    qsa('[data-mobile-menu-toggle]').forEach(b => b.addEventListener('click', () => { menu.classList.add('open'); document.body.style.overflow='hidden'; }));
    qsa('[data-mobile-menu-close]').forEach(b => b.addEventListener('click', () => { menu.classList.remove('open'); if (!qs('.cart-drawer.open')) document.body.style.overflow=''; }));
  }

  function initPredictiveSearch() {
    const form = qs('[data-predictive-search]');
    if (!form || form.dataset.predictiveSearch !== 'true') return;
    const input = qs('[data-search-input]', form);
    const results = qs('[data-search-results]', form);
    if (!input || !results) return;

    const opts = window.predictiveSearchSettings || { showPrice: true, showImage: true };
    let timer;

    function escapeHtml(str) {
      return (str || '').replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));
    }

    function render(products) {
      if (!products.length) {
        results.innerHTML = '<div class="predictive-search__empty">No matches found.</div>';
        results.classList.add('open');
        return;
      }
      results.innerHTML = products.map(p => `
        <a class="predictive-search__item" href="${p.url}">
          ${opts.showImage && p.image ? `<img src="${p.image}" alt="${escapeHtml(p.title)}" loading="lazy">` : ''}
          <div>
            <div class="predictive-search__item-title">${escapeHtml(p.title)}</div>
            ${opts.showPrice ? `<div class="predictive-search__item-price">${p.price}</div>` : ''}
          </div>
        </a>
      `).join('');
      results.classList.add('open');
    }

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const term = input.value.trim();
      if (term.length < 2) { results.classList.remove('open'); results.innerHTML = ''; return; }
      results.innerHTML = '<div class="predictive-search__loading">Searching…</div>';
      results.classList.add('open');
      timer = setTimeout(async () => {
        try {
          const base = window.routes?.predictive_search_url || '/search/suggest';
          const url = `${base}.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,tag`;
          const res = await fetch(url);
          const data = await res.json();
          const products = data?.resources?.results?.products || [];
          render(products);
        } catch (_) {
          results.classList.remove('open');
        }
      }, 280);
    });

    document.addEventListener('click', e => {
      if (!form.contains(e.target)) { results.classList.remove('open'); }
    });
  }

  document.addEventListener('submit', e => {
    const form = e.target;
    if (form.matches('form[action*="/cart/add"]') && form.hasAttribute('data-product-form')) { e.preventDefault(); addToCart(form); }
  });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') { closeDrawer(); qsa('[data-size-chart].is-open [data-size-chart-close]').forEach(x => x.click()); } });
  document.addEventListener('DOMContentLoaded', async () => {
    bindCartTriggers(); bindCartQuantity(); bindCartNote(); bindQuantity(); bindMobileMenu(); initSizeCharts(); initPredictiveSearch(); qsa('[data-product-section]').forEach(initProduct);
    try { updateCartCount(await getCart()); } catch (_) {}
  });
})();
